import { DashboardAPI } from '@business/api/dashboard';
import { ReportAPI } from '@business/api/report';
import { DashboardId, DashboardEmailResponse, DashboardEmailId } from '@business/entities/dashboard';
import { ReportEmailId, ReportEmailResponse } from '@business/entities/report';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

export class ReportEmailBloc extends BaseBloc {
  private readonly dashboardRepo = new Repository<ReportEmailResponse>({
    getItemId: dashboard => dashboard.id,
  });

  readonly reportEmails$ = this.dashboardRepo.items$;
  selectEmail = this.dashboardRepo.selectItem;

  onReset() {
    this.dashboardRepo.reset();
  }

  fetchReportEmails = (id: ReportEmailId) => {
    return ReportAPI.fetchReportEmails(id).pipe(
      this.dashboardRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_dashboard_emails_error') }),
    );
  };

  fetchReportEmail = (id: ReportEmailId) => {
    return ReportAPI.fetchReportEmail(id).pipe(
      this.dashboardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_dashboard_emails_error') }),
    );
  };

  createReportEmail = (id: DashboardId, data: Partial<DashboardEmailResponse>) => {
    return ReportAPI.createReportEmail(id, data).pipe(
      this.dashboardRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_dashboard_email'),
      }),
    );
  };

  updateReportEmail = (id: DashboardEmailId, data: Partial<DashboardEmailResponse>) => {
    return ReportAPI.updateReportEmail(id, data).pipe(
      this.dashboardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_dashboard_email'),
      }),
    );
  };

  deleteReportEmail = (id: ReportEmailId) => {
    return showDeleteConfirmation('Delete Emails', 'Do you really want to remove this report email?').pipe(
      switchMap(() => ReportAPI.deleteReportEmail(id)),
      this.dashboardRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_dashboard_email'),
      }),
    );
  };
}
